import { Injectable } from "@angular/core";
import { Http } from '@angular/http';
import { DomSanitizer, Title, Meta } from '@angular/platform-browser';
import { GeoServices } from '../../services/geo.service';
import { UtilService } from '../../services/util.service';
import { CustomerConfig, CustomerTemplate, CustomerTemplateState, ModelState } from '../../shared/models/customer-config';
import { CustomerStyle } from 'nm-socle-application/app/shared/models/customer-style';
import { BehaviorSubject, Observable, Subscription } from 'rxjs';
import { TemplateCurrentState } from '../models/template-current-state';
import { TranslateService } from 'ng2-translate';

@Injectable()
export class TemplateInitService {
    public static customLoginPageUrl:string;
    public customerConfig$:BehaviorSubject<CustomerConfig>;
    public currentState$:BehaviorSubject<TemplateCurrentState>;
    public currentState:TemplateCurrentState;
    public templateStates$:BehaviorSubject<CustomerTemplateState[]>;
    public templateType$:BehaviorSubject<string>;

    public templateLoading:boolean = false;

    /**
     * @constructor
     * @description
     *
     */
    constructor(
        private _geoServices:GeoServices,
        private _http: Http,
        private _domSanitizer: DomSanitizer,
        private _utilService:UtilService,
        private _translateService:TranslateService,
        private titleService: Title,
        private meta: Meta,
    ) {
        this.customerConfig$ = new BehaviorSubject(this._getDefaultStyle(false));
        this.currentState$ = new BehaviorSubject(new TemplateCurrentState());
        this.templateType$ = new BehaviorSubject(CustomerTemplate.TEMPLATE_HOME_PAGE);
        this.templateStates$ = new BehaviorSubject([]);

        this.customerConfig$.subscribe(custConf => {
            this.templateType$.next(custConf.homePage.templateType);

            this.templateStates$.next(custConf.homePage.states);

            this.setCurrentState(new TemplateCurrentState({state: custConf.homePage.defaultState}));
        });
    }

    public loadTemplate(customLoginPageUrl:any):Observable<CustomerConfig> {
        let observable:Observable<CustomerConfig> = Observable.of(this._getDefaultStyle());

        if(!this.templateLoading) {
            this.templateLoading = true;

            if (customLoginPageUrl) {
                customLoginPageUrl = customLoginPageUrl.value.replace(/\/+$/, '');

                TemplateInitService.customLoginPageUrl = customLoginPageUrl;

                observable = this._http.get(TemplateInitService.customLoginPageUrl + '/config.json')
                    .map((res) => {
                        return this._geoServices.extractData(res)})
                    .catch(error => {
                        return Observable.of(this._getDefaultStyle());
                    }).flatMap((res) => {
                        return this._http.get(customLoginPageUrl + '/i18n/client.'+this._translateService.getDefaultLang()+'.json')
                        .map((resLang) => {
                            let customLang:any = this._geoServices.extractData(resLang);

                            let mainLang:Subscription = this._translateService.getTranslation(this._translateService.getDefaultLang()).subscribe((theLang) => {
                                mainLang.unsubscribe();

                                theLang = this._applyLang(theLang, customLang);
                            });

                            return res;
                        })
                        .catch(error => {
                            return Observable.of(res);
                        });
                    });

                    observable.subscribe(
                    res => {
                        let curCustomConf:CustomerConfig = this._getStandardCustomerConfig();

                        curCustomConf.pageUrl = this._domSanitizer.bypassSecurityTrustResourceUrl(TemplateInitService.customLoginPageUrl + '/index.html');

                        let hasTemplate:boolean = false;
                        let hasBgImg:boolean = false;

                        if(res.backgroundImage != undefined) {
                            curCustomConf.backgroundImage = res.backgroundImage;
                            curCustomConf.pageUrl = undefined;
                            hasBgImg = true;

                            delete res.backgroundImage;
                        }

                        if(res.homePage != undefined) {
                            curCustomConf.homePage.globalCustomize(res.homePage);
                            hasTemplate = true;

                            delete res.homePage;
                        }

                        if(res.styles != undefined) {
                            curCustomConf.styles = res.styles;

                            delete res.styles;
                        }

                        if(res.seo != undefined) {
                            this._displaySeo(res.seo);

                            delete res.seo;
                        }

                        //Affect to style what is not used before
                        for (let property in res) {
                            curCustomConf.styles[property] = res[property];
                        }

                        for(let extStyle of curCustomConf.homePage.externalStyles) {
                            this._utilService.loadExternalStyle(extStyle.id, extStyle.url);
                        }

                        this.customerConfig$.next(curCustomConf);

                        return curCustomConf;
                    }, err => {
                    });
            } else {
                observable = this._setDefaultStyle();
            }

            return observable.share();
        }

        return Observable.of(undefined).share();
    }

    private _applyLang(standardLang:any, customLang:any):any {
        if(typeof customLang == 'object') {
            for(let entry in customLang) {
                if(standardLang[entry] == undefined) {
                    standardLang[entry] = customLang[entry];
                } else {
                    standardLang[entry] = this._applyLang(standardLang[entry], customLang[entry]);
                }
            }
        } else if(typeof customLang == 'string') {
            standardLang = customLang;
        }

        return standardLang;
    }

    private _getDefaultStyle(hasBackground:boolean = true):CustomerConfig {
        let defaultConf:any = {backgroundImage:''};

        if(hasBackground) {
            defaultConf = {};
        }

        let custConfig:CustomerConfig = this._getStandardCustomerConfig(defaultConf);

        return custConfig;
    }

    private _setDefaultStyle(hasBackground:boolean = true):Observable<CustomerConfig> {
        let stdCustConfig:CustomerConfig = this._getDefaultStyle(hasBackground);
        this.customerConfig$.next(stdCustConfig);

        return Observable.of(stdCustConfig);
    }

    public setCurrentState(state:TemplateCurrentState) {
        this.currentState = state;
        this.currentState$.next(state);
    }

    public getState(stateName: string):CustomerTemplateState {
        let tpl:CustomerTemplateState;

        for(let curTpl of this.templateStates$.value) {
            if(curTpl.name == stateName) {
                tpl = curTpl;

                break;
            }
        }

        return tpl;
    }

    public parseCustomerStyle(rawStyle:any, props:string[]):any {
        let customStyles:any = {};

        for(let styName of props) {
            if(rawStyle[styName] != undefined) {
                customStyles[styName] = rawStyle[styName];

                if(CustomerStyle.positions.indexOf(styName) >= 0) {
                    customStyles['position'] = 'absolute';
                }
            }
        }

        return customStyles;
    }

    private _displaySeo(config) {
        for (let tag in config) {
            if (tag == "metas") {
                for (let meta in config.metas) {
                    this.meta.addTag({ name: meta, content: config.metas[meta]});
                }
            }else if (tag == "title") {
                this.titleService.setTitle(config.title);
            }
        }
    }

    public get templateType():string {
        return this.customerConfig$.value.homePage.templateType;
    }

    private _getStandardCustomerConfig(fields?: Partial<CustomerConfig>):CustomerConfig {
        let custConfig:CustomerConfig = new CustomerConfig(fields);

        custConfig.homePage.defaultState = 'login';
        custConfig.homePage.addUpdateElem(ModelState.login);

        return custConfig;
    }
}



// WEBPACK FOOTER //
// /home/gitlab-runner/builds/VyQQATNu/0/groupe-dev/nm17/node_modules/nm-socle-application/app/shared/services/template-init.service.ts